// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import styled from 'styled-components';
import { i18n } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Button from 'shared/components/common/Button';
import * as Actions from 'main-app/store/Actions';
import UpdateCompanyMutation from 'main-app/mutations/UpdateCompany';
import AddressAutocomplete from 'main-app/components/AddressAutocomplete';

const Container = styled.div`
  padding: 24px;
  max-width: 700px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const Description = styled.p`
  font-size: 13px;
  color: #666;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  margin-top: 24px;
  color: #333;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormFieldFull = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: 1 / -1;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${p => (p.configured ? '#f0fff0' : '#fff7e6')};
  color: ${p => (p.configured ? '#00bd83' : '#e67e22')};
  margin-bottom: 16px;
`;

type State = {
  upsClientId: string,
  upsClientSecret: string,
  upsAccountNumber: string,
  upsShipperName: string,
  upsShipperPhone: string,
  upsShipperAddressLine1: string,
  upsShipperAddressLine2: string,
  upsShipperAddressCity: string,
  upsShipperAddressState: string,
  upsShipperAddressZip: string,
  upsShipperAddressCountry: string,
  saving: boolean,
  initialized: boolean,
};

class UPSSettings extends PureComponent<{}, State> {
  state = {
    upsClientId: '',
    upsClientSecret: '',
    upsAccountNumber: '',
    upsShipperName: '',
    upsShipperPhone: '',
    upsShipperAddressLine1: '',
    upsShipperAddressLine2: '',
    upsShipperAddressCity: '',
    upsShipperAddressState: '',
    upsShipperAddressZip: '',
    upsShipperAddressCountry: 'US',
    saving: false,
    initialized: false,
  };

  initFromCompany = (company: Object) => {
    if (this.state.initialized) return;
    this.setState({
      upsClientId: company.upsClientId || '',
      upsClientSecret: company.upsClientSecret || '',
      upsAccountNumber: company.upsAccountNumber || '',
      upsShipperName: company.upsShipperName || '',
      upsShipperPhone: company.upsShipperPhone || '',
      upsShipperAddressLine1: company.upsShipperAddressLine1 || '',
      upsShipperAddressLine2: company.upsShipperAddressLine2 || '',
      upsShipperAddressCity: company.upsShipperAddressCity || '',
      upsShipperAddressState: company.upsShipperAddressState || '',
      upsShipperAddressZip: company.upsShipperAddressZip || '',
      upsShipperAddressCountry: company.upsShipperAddressCountry || 'US',
      initialized: true,
    });
  };

  handleChange = (field: string, value: string) => {
    this.setState({ [field]: value });
  };

  handleSave = async () => {
    this.setState({ saving: true });
    try {
      await UpdateCompanyMutation.commit({
        variables: {
          input: {
            upsClientId: this.state.upsClientId || null,
            upsClientSecret: this.state.upsClientSecret || null,
            upsAccountNumber: this.state.upsAccountNumber || null,
            upsShipperName: this.state.upsShipperName || null,
            upsShipperPhone: this.state.upsShipperPhone || null,
            upsShipperAddressLine1: this.state.upsShipperAddressLine1 || null,
            upsShipperAddressLine2: this.state.upsShipperAddressLine2 || null,
            upsShipperAddressCity: this.state.upsShipperAddressCity || null,
            upsShipperAddressState: this.state.upsShipperAddressState || null,
            upsShipperAddressZip: this.state.upsShipperAddressZip || null,
            upsShipperAddressCountry: this.state.upsShipperAddressCountry || null,
          },
        },
      });
      Actions.alertNotification(i18n.t('UPS settings saved successfully.'), 'success');
    } catch (e) {
      Actions.alertNotification(i18n.t('Error saving UPS settings.'), 'error');
    }
    this.setState({ saving: false });
  };

  render() {
    return (
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{ force: true }}
        query={graphql`
          query UPSSettingsQuery {
            viewer {
              id
              company {
                id
                upsClientId
                upsClientSecret
                upsAccountNumber
                upsShipperName
                upsShipperPhone
                upsShipperAddressLine1
                upsShipperAddressLine2
                upsShipperAddressCity
                upsShipperAddressState
                upsShipperAddressZip
                upsShipperAddressCountry
              }
            }
          }
        `}
        variables={{}}
        render={({ props }) => {
          if (!props) return <Loader />;

          this.initFromCompany(props.viewer.company);
          const isConfigured = !!(this.state.upsClientId && this.state.upsClientSecret && this.state.upsAccountNumber);

          return (
            <Container>
              <Title>{i18n.t('UPS Shipping Integration')}</Title>
              <Description>
                {i18n.t('Connect your UPS account to enable rate shopping, label printing, and package tracking directly from shipments.')}
              </Description>

              <StatusBadge configured={isConfigured}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: isConfigured ? '#00bd83' : '#e67e22' }} />
                {isConfigured ? i18n.t('Connected') : i18n.t('Not Configured')}
              </StatusBadge>

              <SectionTitle>{i18n.t('API Credentials')}</SectionTitle>
              <FormGrid>
                <FormField>
                  <Label>{i18n.t('Client ID')}</Label>
                  <Input
                    type="text"
                    value={this.state.upsClientId}
                    onChange={e => this.handleChange('upsClientId', e.target.value)}
                    placeholder={i18n.t('Enter UPS Client ID')}
                  />
                </FormField>
                <FormField>
                  <Label>{i18n.t('Client Secret')}</Label>
                  <Input
                    type="password"
                    value={this.state.upsClientSecret}
                    onChange={e => this.handleChange('upsClientSecret', e.target.value)}
                    placeholder={i18n.t('Enter UPS Client Secret')}
                  />
                </FormField>
                <FormField>
                  <Label>{i18n.t('Account Number')}</Label>
                  <Input
                    type="text"
                    value={this.state.upsAccountNumber}
                    onChange={e => this.handleChange('upsAccountNumber', e.target.value)}
                    placeholder={i18n.t('Enter UPS Account Number')}
                  />
                </FormField>
              </FormGrid>

              <SectionTitle>{i18n.t('Shipper Address')}</SectionTitle>
              <Description style={{ marginTop: 0 }}>
                {i18n.t('This is the "Ship From" address used when purchasing UPS labels. If left empty, your company address will be used.')}
              </Description>
              <FormGrid>
                <FormField>
                  <Label>{i18n.t('Name')}</Label>
                  <Input
                    type="text"
                    value={this.state.upsShipperName}
                    onChange={e => this.handleChange('upsShipperName', e.target.value)}
                    placeholder={i18n.t('Shipper name')}
                  />
                </FormField>
                <FormField>
                  <Label>{i18n.t('Phone')}</Label>
                  <Input
                    type="text"
                    value={this.state.upsShipperPhone}
                    onChange={e => this.handleChange('upsShipperPhone', e.target.value)}
                    placeholder={i18n.t('Phone number')}
                  />
                </FormField>
                <FormFieldFull>
                  <Label>{i18n.t('Address Line 1')}</Label>
                  <AddressAutocomplete
                    value={this.state.upsShipperAddressLine1}
                    onChange={val => this.handleChange('upsShipperAddressLine1', val)}
                    onAddressSelect={({ line1, city, state, zip }) => {
                      if (line1) this.handleChange('upsShipperAddressLine1', line1);
                      if (city) this.handleChange('upsShipperAddressCity', city);
                      if (state) this.handleChange('upsShipperAddressState', state);
                      if (zip) this.handleChange('upsShipperAddressZip', zip);
                    }}
                    placeholder={i18n.t('Street address')}
                    style={{ paddingBottom: 0 }}
                  />
                </FormFieldFull>
                <FormFieldFull>
                  <Label>{i18n.t('Address Line 2')}</Label>
                  <Input
                    type="text"
                    value={this.state.upsShipperAddressLine2}
                    onChange={e => this.handleChange('upsShipperAddressLine2', e.target.value)}
                    placeholder={i18n.t('Suite, unit, etc.')}
                  />
                </FormFieldFull>
                <FormField>
                  <Label>{i18n.t('City')}</Label>
                  <Input
                    type="text"
                    value={this.state.upsShipperAddressCity}
                    onChange={e => this.handleChange('upsShipperAddressCity', e.target.value)}
                  />
                </FormField>
                <FormField>
                  <Label>{i18n.t('State')}</Label>
                  <Input
                    type="text"
                    value={this.state.upsShipperAddressState}
                    onChange={e => this.handleChange('upsShipperAddressState', e.target.value)}
                    placeholder="CA"
                  />
                </FormField>
                <FormField>
                  <Label>{i18n.t('Zip Code')}</Label>
                  <Input
                    type="text"
                    value={this.state.upsShipperAddressZip}
                    onChange={e => this.handleChange('upsShipperAddressZip', e.target.value)}
                  />
                </FormField>
                <FormField>
                  <Label>{i18n.t('Country')}</Label>
                  <Input
                    type="text"
                    value={this.state.upsShipperAddressCountry}
                    onChange={e => this.handleChange('upsShipperAddressCountry', e.target.value)}
                    placeholder="US"
                  />
                </FormField>
              </FormGrid>

              <div style={{ marginTop: 24 }}>
                <Button
                  theme="blue"
                  loading={this.state.saving}
                  onClick={this.handleSave}
                >
                  {i18n.t('Save UPS Settings')}
                </Button>
              </div>
            </Container>
          );
        }}
      />
    );
  }
}

export default UPSSettings;
